import { ArrowLeft, ArrowRight, Browser, Check, DownloadSimple, PuzzlePiece, ShieldCheck } from "@phosphor-icons/react";
import { BrandMark } from "./Brand";

const DOWNLOAD_URL = "/downloads/echolab-browser-extension-v0.3.2.zip";
const EXTENSION_VERSION = "0.3.2";
const EXTENSION_SHA256 = "32a2f9588da8849d6eeeab483bc8b9d9647a630b19bbfe317c101832bdadd89a";

const installationSteps = [
  "点击下载按钮，得到 echolab-browser-extension-v0.3.2.zip。",
  "双击 ZIP 文件解压，保留解压后的完整文件夹。",
  "在 Chrome 地址栏输入 chrome://extensions 并回车。",
  "打开页面右上角的“开发者模式”。",
  "如果已经安装旧版 EchoLab 插件，先在旧插件卡片上点击“移除”。",
  "点击“加载已解压的扩展程序”。",
  "选择刚刚解压、且里面直接包含 manifest.json 的文件夹。",
  "在 Chrome 扩展菜单中把 EchoLab Browser Relay 固定到工具栏。",
  "回到 EchoLab 刷新页面，看到“浏览器插件已就绪”即可。",
];

const permissions = [
  ["activeTab", "只在你主动打开插件时识别当前标签页。"],
  ["debugger", "在已配对标签页中执行观察、点击、输入、导航、等待和截图。"],
  ["tabs", "显示当前标签页的标题和网址，帮助你确认连接目标。"],
  ["storage", "在本机保存 Relay 地址和当前会话 ID，不保存 Maxwell Key。"],
  ["EchoLab 官网", "只在 EchoLab 官方页面接收你主动发起的一键连接请求。"],
];

export function ExtensionPage({ standalone = false, onCreateEvaluation }: { standalone?: boolean; onCreateEvaluation?: () => void }) {
  const content = (
    <div className="extension-page">
      <div className="page-intro">
        <h2>EchoLab 浏览器插件</h2>
        <p>安装一次后，EchoLab 可以自动打开目标网页、完成连接并启动评测。当前采用开发者模式手动安装。</p>
      </div>

      <section className="panel extension-release">
        <div className="extension-release-icon" aria-hidden="true"><BrandMark /></div>
        <div className="extension-release-copy">
          <span className="config-type">Chrome Extension · Manifest V3</span>
          <h3>EchoLab Browser Relay</h3>
          <p>版本 {EXTENSION_VERSION} · Chrome 116 及以上 · ZIP 约 21 KB · 支持一键连接</p>
        </div>
        <a className="button button-primary button-large" href={DOWNLOAD_URL} download>
          <DownloadSimple size={19} />下载插件 ZIP
        </a>
      </section>

      <section className="extension-layout">
        <div className="panel install-panel">
          <div className="panel-heading"><div><h2>安装到 Google Chrome</h2><p>从 0.1.0 更新时，请先移除旧插件，再加载新文件夹。</p></div></div>
          <ol className="install-steps">
            {installationSteps.map((step, index) => (
              <li key={step}><span>{index + 1}</span><p>{step}</p></li>
            ))}
          </ol>
          <div className="chrome-path"><span>Chrome 扩展管理页</span><code>chrome://extensions</code></div>
        </div>

        <div className="extension-side-stack">
          <section className="panel extension-feature-panel">
            <div className="panel-heading"><div><h2>当前版本能力</h2><p>版本 {EXTENSION_VERSION}</p></div></div>
            <ul className="feature-list">
              <li><Check size={17} />观察当前页面内容和状态</li>
              <li><Check size={17} />点击、输入、导航、等待和截图</li>
              <li><Check size={17} />持续监听 Agent 回复，稳定后再进入下一轮</li>
              <li><Check size={17} />普通文字快速确认，画布与工具变化保持安全观察</li>
              <li><Check size={17} />“已提交”或输入暂时禁用时继续监听，不误判为结束</li>
              <li><Check size={17} />长任务回复检查点与断线恢复</li>
              <li><Check size={17} />自动检测插件版本与就绪状态</li>
              <li><Check size={17} />一键打开目标网页并完成配对</li>
              <li><Check size={17} />人工停止后立即解除标签页控制</li>
            </ul>
          </section>
          <section className="panel extension-safety">
            <ShieldCheck size={24} />
            <div><strong>只控制这次评测的目标标签页</strong><p>插件不会读取 Chrome 保存的密码或完整浏览器资料；但 Agent 可以看到并操作被测网页中当前可见的内容。</p></div>
          </section>
        </div>
      </section>

      <section className="panel permission-panel">
        <div className="panel-heading"><div><h2>为什么需要这些权限</h2><p>权限来自安装包中的 manifest.json，可在安装前自行检查。</p></div></div>
        <div className="permission-list">
          {permissions.map(([name, description]) => <div key={name}><code>{name}</code><p>{description}</p></div>)}
        </div>
      </section>

      <section className="extension-usage">
        <Browser size={22} />
        <div><strong>安装后怎么用？</strong><p>刷新 EchoLab，确认页面显示“浏览器插件已就绪”，然后选择模板并点击“创建并开始评测”。其余连接步骤会自动完成。</p></div>
        {onCreateEvaluation && <button className="button button-secondary" onClick={onCreateEvaluation}>去创建评测<ArrowRight size={17} /></button>}
      </section>

      <details className="release-integrity">
        <summary>安装包校验信息</summary>
        <div><span>SHA-256</span><code>{EXTENSION_SHA256}</code></div>
      </details>
    </div>
  );

  if (!standalone) return content;
  return (
    <div className="public-extension-shell">
      <header>
        <a className="login-brand" href="/" aria-label="EchoLab 首页"><span className="brand-mark" aria-hidden="true"><BrandMark /></span> EchoLab</a>
        <a className="button button-secondary" href="/"><ArrowLeft size={17} />返回 EchoLab</a>
      </header>
      <main>{content}</main>
    </div>
  );
}
